class BasicPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("[data-predictive-search-input]");
    this.resultsContainer = this.querySelector(
      "[data-predictive-search-results]"
    );
    this.productsContainer = this.querySelector("[data-products-container]");
    this.productTemplate = this.querySelector("[data-product-template]");

    this.isOpen = false;
    this.searchTerm = "";
    this.abortController = new AbortController();

    // Buscar el botón de búsqueda móvil con el selector correcto
    this.searchToggle = document.querySelector('[id*="search-toggle"]');
    // console.log("🔍 Search toggle found:", this.searchToggle);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("input", this.onChange.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    // Add search toggle listener
    if (this.searchToggle) {
      this.searchToggle.addEventListener("click", () => {
        this.toggleSearch();
      });
    }

    // Add escape key listener to close search
    document.addEventListener("keydown", (event) => {
      // console.log("🔑 Key pressed:", event.key, "isOpen:", this.isOpen);
      if (event.key === "Escape" && this.isOpen) {
        // console.log("🚪 ESC pressed - calling close(true)");
        this.close(true);
      }
    });

    // Add click outside listener
    document.addEventListener("click", (event) => {
      // console.log("🖱️ Click detected - isOpen:", this.isOpen);

      if (this.isOpen && !this.contains(event.target)) {
        // Solo verificar searchToggle si existe
        const clickedOnToggle =
          this.searchToggle && this.searchToggle.contains(event.target);

        if (!clickedOnToggle) {
          // console.log("🚪 Click outside - calling close()");
          this.close();
        }
      }
    });
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
      return;
    }
    // Normalizamos la referencia antes de ir a /search (ej. "LG-280" → "LG280"),
    // porque la página de resultados sí distingue los guiones.
    const term = this.getQuery();
    const normalized = this.normalizeTerm(term);
    if (normalized && normalized.toLowerCase() !== term.toLowerCase()) {
      this.input.value = normalized;
    }
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    if (!newSearchTerm.length) {
      this.clearResults();
      return;
    }

    this.searchTerm = newSearchTerm;
    this.getSearchResults(this.searchTerm);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.resultsContainer.hasChildNodes()) {
      this.open();
    }
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  /* Normaliza referencias tipo "LG-280" / "BD 280" → "LG280" para que el buscador
     de Shopify las case con títulos donde van pegadas (ej. "LG280EZC"). */
  normalizeTerm(term) {
    return term.replace(/[-_./]+/g, "").replace(/\s+/g, " ").trim();
  }

  async fetchSuggest(term, signal) {
    const response = await fetch(
      `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(
        term
      )}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`,
      { signal }
    );
    if (!response.ok) throw new Error(response.status);
    const data = await response.json();
    return (
      (data.resources && data.resources.results && data.resources.results.products) || []
    );
  }

  async getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    try {
      const normalized = this.normalizeTerm(searchTerm);
      const queries = [searchTerm];
      // Solo lanzamos la 2ª consulta si la normalización cambia algo (guiones, puntos...)
      if (normalized && normalized.toLowerCase() !== searchTerm.toLowerCase()) {
        queries.push(normalized);
      }

      const listas = await Promise.all(queries.map((q) => this.fetchSuggest(q, signal)));
      // Si hay variante normalizada, sus resultados van primero (es lo que suele buscar el cliente)
      const ordenadas = listas.length > 1 ? [listas[1], listas[0]] : listas;

      const vistos = new Set();
      const products = [];
      ordenadas.forEach((lista) =>
        lista.forEach((p) => {
          if (!vistos.has(p.id)) {
            vistos.add(p.id);
            products.push(p);
          }
        })
      );

      this.renderResults(products.slice(0, 4), searchTerm);
    } catch (error) {
      if (error.name === "AbortError") return;
      this.close();
      throw error;
    }
  }

  renderResults(products, searchTerm) {
    this.clearResults();


    if (products.length > 0) {
      this.productsContainer.hidden = false;
      const productsList = this.productsContainer.querySelector("ul");
      products.forEach((product, index) => {
        const item = this.productTemplate.content.cloneNode(true);
        const li = item.querySelector("li");
        const link = item.querySelector("a");
        const image = item.querySelector("img");
        const title = item.querySelector(".predictive-search-item__title");
        const price = item.querySelector(".predictive-search-item__price");

        li.id = `predictive-search-option-${index}`;
        link.href = product.url;
        if (product.featured_image) {
          image.src = product.featured_image.url;
          image.alt = product.featured_image.alt || product.title;
        }
        title.textContent = product.title;
        price.textContent = product.price;

        productsList.appendChild(item);
      });

      this.appendViewAllLink(searchTerm);
      this.resultsContainer.hidden = false;
      this.open();
    } else {
      this.resultsContainer.hidden = true;
    }
  }

  switchOption(direction) {
    if (!this.isOpen) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll("li")).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a');
    if (selectedOption) selectedOption.click();
  }

  appendViewAllLink(searchTerm) {
    const contenedor =
      this.resultsContainer.querySelector(".predictive-search-results-content") ||
      this.resultsContainer;
    const a = document.createElement("a");
    a.className = "predictive-search-view-all";
    const normalizado = this.normalizeTerm(searchTerm);
    const consulta =
      normalizado && normalizado.toLowerCase() !== searchTerm.toLowerCase()
        ? normalizado
        : searchTerm;
    a.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(
      consulta
    )}&options[prefix]=last`;
    a.textContent = `Ver todos los resultados de \u00ab${searchTerm}\u00bb`;
    contenedor.appendChild(a);
  }

  clearResults() {
    // console.log("🧹 Clearing results");
    this.productsContainer.hidden = true;
    this.resultsContainer.hidden = true;

    this.querySelectorAll("ul").forEach((ul) => (ul.innerHTML = ""));
    this.querySelectorAll(".predictive-search-view-all").forEach((el) => el.remove());
    this.input.setAttribute("aria-activedescendant", "");
  }

  open() {
    // console.log("🔓 Opening search");
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.classList.add("is-open");
  }

  toggleSearch() {
    if (this.isOpen) {
      this.close();
    } else {
      this.input.value = "";
      this.clearResults();
      this.input.setAttribute("aria-expanded", true);
      this.isOpen = true;
      this.classList.add("is-open");

      // Solo modificar searchToggle si existe
      if (this.searchToggle) {
        this.searchToggle.setAttribute("aria-expanded", true);
      }
      this.input.focus();
    }
  }

  close(clearSearchTerm = false) {
    // console.log("🔒 Closing search, clearSearchTerm:", clearSearchTerm);

    if (clearSearchTerm) {
      this.input.value = "";
      this.searchTerm = "";
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-expanded", false);
    this.input.setAttribute("aria-activedescendant", "");
    this.isOpen = false;
    this.classList.remove("is-open");

    // Solo modificar searchToggle si existe
    if (this.searchToggle) {
      this.searchToggle.setAttribute("aria-expanded", false);
    }

    // Llamar clearResults para ocultar los resultados
    this.clearResults();
    // console.log("✅ Search closed and results cleared");
  }
}

customElements.define("basic-predictive-search", BasicPredictiveSearch);
